import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { getSolution } from "@/data/solutions";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";

export default function SolutionDetail() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const solution = getSolution(slug);
  usePageTitle(solution ? `${solution[lang].name} | Scutul Fermierului` : t.meta.solutions);

  if (!solution) return <Navigate to="/solutii" replace />;

  const c = solution[lang];

  return (
    <div data-testid={`solution-detail-${slug}`} className="pt-24">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={solution.image} alt={c.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pb-24 pt-20 md:px-10 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              data-testid="solution-back-link"
              to="/solutii"
              className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-bronze"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              {t.solutionsPage.back}
            </Link>
            <p className="mt-10 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
              <span className="h-px w-10 bg-bronze" />
              {t.solutionsPage.detailOverline}
            </p>
            <h1 className="mt-6 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight text-ivory md:text-6xl lg:text-7xl">
              {c.name}
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">{c.short}</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.solutionsPage.problem}</p>
            <div className="mt-6 space-y-6">
              {c.problem.map((p, i) => (
                <p key={i} className={i === 0 ? "font-serif text-2xl leading-snug text-ink md:text-3xl" : "text-base leading-relaxed text-stone md:text-lg"}>
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="border border-line bg-sand p-8 md:p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.solutionsPage.what}</p>
              <ul className="mt-8 space-y-5">
                {c.what.map((item, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-bronze/40 bg-ivory">
                      <Check className="h-3.5 w-3.5 text-bronze" />
                    </span>
                    <span className="text-sm leading-relaxed text-ink/90 md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-sand/30">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.solutionsPage.steps}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {c.steps.map((step, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="group h-full border border-line bg-sand p-8 transition-colors duration-500 hover:border-bronze/40">
                  <span className="font-serif text-5xl text-bronze/25 transition-colors duration-500 group-hover:text-bronze/60">
                    0{i + 1}
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-ink">{step.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone">{step.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection heading={t.solutionsPage.ctaHeading} sub={t.solutionsPage.ctaSub} />
    </div>
  );
}
